import {
  Advance as GqlAdvance,
  AdvanceType as GqlAdvanceType,
  Charge as GqlCharge,
  Fee as GqlFee,
  FeeStatus as GqlFeeStatus,
  SharedAppPaymentDueFragment as GqlPaymentDue,
} from '@willow/graphql-iso/src/app';
import { Advance, Charge, Fee, PaymentDue } from '@willow/types-iso';

import { add } from '../utils/math';

type LiquidationBalances = {
  principalBalance: number;
  outstandingInterest: number; // includes unpaid statement interest + default interest
  escrowAdvances: number;
  corporateAdvances: number;
  fees: number;
};

// Iso charges discriminate via lowercase `type`; Gql charges via `__typename`.
const isAdvance = (c: Charge | GqlCharge): c is Advance | GqlAdvance =>
  '__typename' in c ? c.__typename === 'Advance' : c.type === 'advance';

const isFee = (c: Charge | GqlCharge): c is Fee | GqlFee =>
  '__typename' in c ? c.__typename === 'Fee' : c.type === 'fee';

export const getCurrentLiquidationBalances = ({
  outstandingBalance,
  escrowBalance,
  charges,
  paymentDues,
}: {
  outstandingBalance: number;
  escrowBalance: number;
  charges: (Charge | GqlCharge)[];
  paymentDues: PaymentDue[] | GqlPaymentDue[];
}): LiquidationBalances => {
  const principalBalance = outstandingBalance;
  const outstandingInterest = paymentDues
    .filter((pd) => ('amountRemaining' in pd.status ? pd.status.amountRemaining : pd.status.totalAmountRemaining) > 0)
    .reduce((acc, pd) => add(acc, pd.interest), 0);

  // Escrow balances: add remaining negative escrow balance + any existing escrow advances
  const unpaidEscrow = escrowBalance < 0 ? Math.abs(escrowBalance) : 0;

  const chargedAdvances = charges.filter(isAdvance).filter((c) => ['CHARGED', GqlFeeStatus.Charged].includes(c.status));
  const recordedEscrowAdvances = chargedAdvances
    .filter((c) => c.advanceConfig && ['ESCROW', GqlAdvanceType.Escrow].includes(c.advanceConfig.advanceType))
    .reduce((acc, c) => add(acc, c.amountRemaining), 0);
  const escrowAdvances = add(unpaidEscrow, recordedEscrowAdvances);

  const corporateAdvances = chargedAdvances
    .filter((c) => c.advanceConfig && ['CORPORATE', GqlAdvanceType.Corporate].includes(c.advanceConfig.advanceType))
    .reduce((acc, c) => add(acc, c.amountRemaining), 0);

  const fees = charges
    .filter(isFee)
    .filter((c) => ['CHARGED', GqlFeeStatus.Charged].includes(c.status))
    .reduce((acc, c) => add(acc, c.amountRemaining), 0);

  return { principalBalance, outstandingInterest, escrowAdvances, corporateAdvances, fees };
};
